import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, CircularProgress } from '@mui/material';
import { RootState } from '../../store/store';
import { useSiteInfo, siteInfoResource } from '../../hooks/useSiteInfo';
import ComingSoonPage from '../../pages/ComingSoonPage';
import ClosedShell from './ClosedShell';
import { gateMode, isAlwaysOpenPath, SETTINGS_LINK, SHOP_CLOSED_EVENT } from './shopGateRules';
import '../../pages/coming-soon.css';

/** Staff-előnézet zárt boltban: a bolt felett figyelmeztető sáv. */
export const StaffClosedBanner: React.FC = () => (
  <div className="gtstaffbar" role="status">
    <span>A bolt zárva — csak te látod.</span>
    <Link to={SETTINGS_LINK}>Beállítások</Link>
  </div>
);

interface ShopGateProps {
  /** A teljes bolt (fejléc, lábléc, útvonalak) — nyitott boltban és staff-előnézetben. */
  children: React.ReactNode;
  /** Csak az útvonalak (bolti keret nélkül) — zárt boltban a mindig elérhető oldalakhoz. */
  bareRoutes: React.ReactNode;
}

/**
 * Zárt bolt mód kapuja a webshop-ágon (a /admin és a /muhely külön ágon fut):
 * - nyitva → a bolt;
 * - zárva + staff → a bolt + „A bolt zárva” sáv;
 * - zárva + látogató → „Hamarosan” oldal; a mindig elérhető oldalak (bejelentkezés,
 *   jelszó-visszaállítás, ÁSZF, adatvédelem, hírlevél …) minimális keretben.
 * Ha a beállítás nem tölthető be, zártnak vesszük (a látogató a „Hamarosan” oldalt látja,
 * a staff az előnézetet) — így hiba esetén sem jelenik meg egy üres, nyitottnak tűnő bolt.
 */
const ShopGate: React.FC<ShopGateProps> = ({ children, bareRoutes }) => {
  const { pathname } = useLocation();
  const { data, error } = useSiteInfo();
  const { user, token } = useSelector((state: RootState) => state.auth);
  // Friss állapot a betöltés után: a szerver 503 shop_closed jelzése, illetve a lap
  // újra-fókuszálásakor újraolvasott beállítás (így a közben megnyitott bolt is látszik).
  const [live, setLive] = useState<boolean | undefined>(undefined);

  const refresh = useCallback(() => {
    siteInfoResource.invalidate();
    siteInfoResource.get()
      .then((fresh) => setLive(fresh.shop_open !== false))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    const onClosed = () => {
      setLive(false);
      refresh();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    window.addEventListener(SHOP_CLOSED_EVENT, onClosed);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener(SHOP_CLOSED_EVENT, onClosed);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh]);

  let shopOpen: boolean | undefined = live;
  if (shopOpen === undefined && data) shopOpen = data.shop_open !== false;
  else if (shopOpen === undefined && error) shopOpen = false;

  const mode = gateMode({
    shopOpen,
    isStaff: Boolean(user && user.is_active !== false && (user.is_staff || user.is_superuser)),
    userLoading: Boolean(token) && !user,
  });

  if (mode === 'loading') {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={60} aria-label="Betöltés" />
      </Box>
    );
  }
  if (mode === 'closed') {
    return isAlwaysOpenPath(pathname) ? <ClosedShell>{bareRoutes}</ClosedShell> : <ComingSoonPage />;
  }
  return (
    <>
      {mode === 'staff-preview' && <StaffClosedBanner />}
      {children}
    </>
  );
};

export default ShopGate;
